import React, { useState, useEffect, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { PHASES, SPARKLE_FADE_IN_DURATION, ROTATION_STOP_DURATION, CROSSFADE_DURATION, SPARKLE_MOVE_DURATION } from './constants';

const NRAIMorphSpinner = ({
  size = 'medium',
  color = '#9268F7',
  thickness = 3.6,
  complete = false,
  onComplete,
  text, // Can be a string or a React node/component
  sx = {}
}) => {
  const theme = useTheme();
  const [phase, setPhase] = useState(() => (complete ? PHASES.COMPLETE : PHASES.ENTERING));
  const morphingTimerRef = useRef(null);
  const stopTimerRef = useRef(null);

  const getSizeValue = () => {
    if (typeof size === 'number') return size;
    const sizeMap = { small: 24, medium: 48, large: 64 };
    return sizeMap[size];
  };

  const sizeValue = getSizeValue();
  const iconSize = sizeValue * 0.5;

  // Entrance animation - only runs on mount
  useEffect(() => {
    // If already complete on mount, we're already in complete phase (from initial state)
    if (complete) {
      onComplete?.();
      return;
    }
    // Only transition to active if we're still in entering phase
    if (phase === PHASES.ENTERING) {
      const timer = setTimeout(() => {
        setPhase(prev => {
          // If complete became true while waiting, go to complete
          if (complete) return PHASES.COMPLETE;
          return PHASES.ACTIVE;
        });
      }, SPARKLE_FADE_IN_DURATION);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Forward morphing sequence (thinking → complete)
  useEffect(() => {
    // Only trigger morphing if complete is true
    if (!complete) {
      // Clear any ongoing morphing timers if complete becomes false
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      if (morphingTimerRef.current) {
        clearTimeout(morphingTimerRef.current);
        morphingTimerRef.current = null;
      }
      return;
    }

    // If already complete, ensure we're in complete phase
    if (phase === PHASES.COMPLETE) {
      onComplete?.();
      return;
    }

    // If we're already morphing (stopping or morphing), let the timers continue
    // Don't restart the sequence
    if (phase === PHASES.STOPPING || phase === PHASES.MORPHING) {
      return;
    }

    // If we're in active state, start the morphing sequence
    if (phase === PHASES.ACTIVE) {
      // Clear any existing timers
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
      }
      if (morphingTimerRef.current) {
        clearTimeout(morphingTimerRef.current);
      }

      // Step 1: Stop rotation
      setPhase(PHASES.STOPPING);

      stopTimerRef.current = setTimeout(() => {
        // Step 2: Start morphing (crossfade + sparkle move)
        setPhase(PHASES.MORPHING);

        morphingTimerRef.current = setTimeout(() => {
          // Step 3: Complete
          setPhase(PHASES.COMPLETE);
          stopTimerRef.current = null;
          morphingTimerRef.current = null;
          onComplete?.();
        }, CROSSFADE_DURATION);
      }, ROTATION_STOP_DURATION);

      // Don't return a cleanup function here - we want timers to persist through phase changes
    }
  }, [complete, phase, onComplete]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      if (morphingTimerRef.current) {
        clearTimeout(morphingTimerRef.current);
        morphingTimerRef.current = null;
      }
    };
  }, []);

  // Reverse animation (complete → thinking)
  useEffect(() => {
    // Only reset if complete becomes false AND we're not in the middle of morphing
    // Don't reset if we're actively morphing (stopping/morphing phases)
    if (!complete && phase === PHASES.COMPLETE) {
      // Reset to active state to restart spinner
      setPhase(PHASES.ACTIVE);
    }
  }, [complete, phase]);

  const sparkleOpacity = phase === PHASES.ENTERING ? 0 : 1;
  const spinnerOpacity = [PHASES.ENTERING, PHASES.MORPHING, PHASES.COMPLETE].includes(phase) ? 0 : 1;
  const chevronOpacity = [PHASES.MORPHING, PHASES.COMPLETE].includes(phase) ? 1 : 0;
  const sparkleTop = [PHASES.MORPHING, PHASES.COMPLETE].includes(phase) ? '71%' : '50%';

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        ...sx
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          width: sizeValue,
          height: sizeValue
        }}
      >
        {/* Spinning arc layer */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: spinnerOpacity,
            transition:
              phase === PHASES.STOPPING
                ? 'opacity 200ms ease-out'
                : phase === PHASES.MORPHING
                ? `opacity ${CROSSFADE_DURATION}ms ease-in-out`
                : 'opacity 150ms ease-in-out'
          }}
        >
          <CircularProgress
            variant="determinate"
            value={100}
            size={sizeValue}
            thickness={thickness}
            sx={{
              color: color,
              opacity: 0.05,
              position: 'absolute'
            }}
          />
          <CircularProgress
            size={sizeValue}
            thickness={thickness}
            sx={{
              color: color,
              '& circle': { strokeLinecap: 'round' },
              animation: phase === PHASES.STOPPING ? 'spinnerStop 200ms ease-out forwards' : undefined,
              '@keyframes spinnerStop': {
                from: { transform: 'rotate(0deg)' },
                to: { transform: 'rotate(300deg)' }
              }
            }}
          />
        </Box>

        {/* Completion icon layer (chevron only) */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: sizeValue,
            height: sizeValue,
            opacity: chevronOpacity,
            transition: `opacity ${CROSSFADE_DURATION}ms ease-in-out`
          }}
        >
          <svg width={sizeValue} height={sizeValue} viewBox="0 0 24 24" fill="none">
            <path
              d="M20.9095 12.8838C21.3327 13.112 21.9807 13.0204 22 12.4625L22 8.67664C22 7.90834 21.4496 6.96603 20.775 6.58188C19.0584 5.60703 17.5675 4.75839 15.9094 3.81461L15.9074 3.81348C15.0732 3.33864 14.1963 2.8395 13.2274 2.28827C12.8894 2.09718 12.4452 2 12.0007 2C11.5551 2 11.1109 2.09718 10.7733 2.28827L7.80566 3.97653C6.25921 4.85623 4.83947 5.66384 3.22669 6.58188C2.55174 6.96603 2 7.90834 2 8.67664L2 12.4625C2.01365 12.8355 2.30767 13 2.62733 13C2.78616 13 2.95131 12.9593 3.09183 12.8838L10.7733 8.09046C11.1109 7.89937 11.5551 7.80219 12.0007 7.80219C12.4452 7.80219 12.8894 7.89937 13.2274 8.09046L20.9095 12.8838Z"
              fill={color}
            />
          </svg>
        </Box>

        {/* Center sparkle (moves to bottom during morph) */}
        <Box
          sx={{
            position: 'absolute',
            top: sparkleTop,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: iconSize,
            height: iconSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: sparkleOpacity,
            transition: `opacity 150ms ease-in-out, top ${SPARKLE_MOVE_DURATION}ms ease-in-out`,
            pointerEvents: 'none'
          }}
        >
          <svg width={iconSize} height={iconSize} viewBox="0 0 12 12" fill="none" style={{ display: 'block' }}>
            <path
              d="M7.02539 3.19238C7.37195 3.98733 8.00722 4.62753 8.80664 4.97559V4.97656L11.1582 6.00391L8.81055 7.03027C8.01609 7.37733 7.37589 8.01327 7.02832 8.81348L6.00488 11.1582L5.01953 8.86621C4.67627 8.06682 4.04006 7.42267 3.24414 7.07129H3.24512L0.835938 6.00391L3.19238 4.97656C3.98703 4.62948 4.62717 3.99274 4.97461 3.19238L6 0.84082L7.02539 3.19238Z"
              fill={color}
              stroke={color}
            />
          </svg>
        </Box>
      </Box>
      {text &&
        (typeof text === 'string' ? (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {text}
          </Typography>
        ) : (
          text
        ))}
    </Box>
  );
};

export default NRAIMorphSpinner;
